/* frontend/src/layouts/PublicLayout.js */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Car, Menu, X, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Typography } from '../components/ui/typography';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';

export default function PublicLayout({ children }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-2">
            <Link to="/catalogo" className="flex items-center gap-2">
              <Car className="h-6 w-6 text-primary" />
              <Typography variant="h3" className="font-semibold">
                Seminovos
              </Typography>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/catalogo" className="text-sm font-medium hover:text-primary transition-colors">
              Catálogo
            </Link>
            <a href="#contato" className="text-sm font-medium hover:text-primary transition-colors">
              Contato
            </a>
          </nav>
          
          <div className="flex items-center gap-4">
            <a href="tel:+5511917530290" className="hidden md:flex items-center gap-2 text-sm font-medium">
              <PhoneCall className="h-4 w-4 text-primary" />
              (11) 91753-0290
            </a>
            <Button variant="outline" asChild>
              <Link to="/admin">
                Área Administrativa
              </Link>
            </Button>
            
            {/* Mobile menu trigger */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="right">
                <div className="flex flex-col h-full">
                  <div className="flex items-center justify-between mb-8">
                    <Link to="/catalogo" className="flex items-center gap-2" onClick={() => setMobileMenuOpen(false)}>
                      <Car className="h-6 w-6 text-primary" />
                      <Typography variant="h4" className="font-semibold">
                        Seminovos
                      </Typography>
                    </Link>
                    
                    <Button variant="ghost" size="icon" onClick={() => setMobileMenuOpen(false)}>
                      <X className="h-5 w-5" />
                      <span className="sr-only">Fechar menu</span>
                    </Button>
                  </div>
                  
                  <nav className="flex flex-col space-y-4">
                    <Link 
                      to="/catalogo" 
                      className="text-lg font-medium hover:text-primary transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Catálogo
                    </Link>
                    <a 
                      href="#contato" 
                      className="text-lg font-medium hover:text-primary transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      Contato
                    </a>
                  </nav>
                  
                  <div className="mt-auto pt-6 border-t">
                    <a 
                      href="tel:+5511917530290" 
                      className="flex items-center gap-2 text-lg font-medium mb-4 hover:text-primary transition-colors"
                    >
                      <PhoneCall className="h-5 w-5 text-primary" />
                      (11) 91753-0290
                    </a>
                    
                    <Button className="w-full" asChild>
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                        Área Administrativa
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer */}
      <footer className="border-t bg-muted/40 py-6 md:py-10" id="contato">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Car className="h-6 w-6 text-primary" />
                <Typography variant="h4" className="font-semibold">
                  Seminovos
                </Typography>
              </div>
              <p className="text-muted-foreground">
                Os melhores veículos seminovos para você escolher com confiança 
                e garantia de procedência.
              </p>
            </div>
            
            <div>
              <Typography variant="h5" className="font-semibold mb-4">
                Entre em contato
              </Typography>
              <ul className="space-y-2 text-muted-foreground">
                <li>
                  <a href="mailto:semi.novos@easycarros.com" className="hover:text-primary transition-colors">
                    semi.novos@easycarros.com
                  </a>
                </li>
                <li>
                  <a href="tel:+5511917530290" className="hover:text-primary transition-colors">
                    (11) 91753-0290 - Vendas
                  </a>
                </li>
                <li>
                  <a href="tel:+5511952133649" className="hover:text-primary transition-colors">
                    (11) 95213-3649 - Documentação
                  </a>
                </li>
              </ul>
            </div>
            
            <div>
              <Typography variant="h5" className="font-semibold mb-4">
                Horário de atendimento
              </Typography>
              <ul className="space-y-2 text-muted-foreground">
                <li>Segunda a Sexta: 9h às 18h</li>
              </ul>
            </div>
          </div>
          
          <div className="mt-8 pt-6 border-t">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-center md:text-left text-muted-foreground text-sm">
                © {new Date().getFullYear()} Seminovos. Todos os direitos reservados.
              </p>
              
              <nav className="flex items-center gap-4">
                <Link to="/catalogo" className="text-sm text-muted-foreground hover:text-primary transition-colors">
                  Catálogo
                </Link>
              </nav>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
